/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { Link as ReactRouterLink } from 'react-router';

import { withRetrackedContextAsProps } from '@coursera-web/sharing-common';
import * as Sentry from '@sentry/react';
import type { Location } from 'history';
import keyBy from 'lodash/keyBy';
import { compose, withProps } from 'recompose';

import loggerSingleton from 'js/app/loggerSingleton';
import Retracked from 'js/app/retracked';
import connectToRouter from 'js/lib/connectToRouter';
import type { TrackingContextData } from 'js/lib/retracked';

import type { ButtonProps } from '@coursera/cds-core';
import {
  Button,
  ProductCard as CdsProductCard,
  Divider,
  InlineNotification,
  Pagination,
  Tag,
  Typography,
  Typography2,
  breakpoints,
  useMediaQuery,
} from '@coursera/cds-core';
import { CheckIcon, ChevronPreviousIcon } from '@coursera/cds-icons';
import { Col, LazyRender, Row } from '@coursera/coursera-ui';

import { getProductCardDisplayProps } from 'bundles/browse/utils';
import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import withSingleTracked from 'bundles/common/components/withSingleTracked';
import { Heading, Section } from 'bundles/enterprise-legacy-learner-home/components/AutoHeading';
import InformationCardToggle from 'bundles/enterprise-legacy-learner-home/components/InformationCard/InformationCardToggle';
import { PRODUCT_TYPE } from 'bundles/enterprise-legacy-learner-home/components/ProductCard';
import { ProductCardCds } from 'bundles/enterprise-legacy-learner-home/components/ProductCardCds';
import SkillTargetsInformationCard from 'bundles/enterprise-legacy-learner-home/components/StaticInformationCards/SkillTargetsInformationCard';
import SkillsClipsCollection from 'bundles/enterprise-legacy-learner-home/components/single-program/SkillsClipsCollection';
import { SKILLS_IDS_FOR_CLIPS_ALLOW_LIST } from 'bundles/enterprise-legacy-learner-home/constants/skillCollectionsConstants';
import type { Product, ProductType } from 'bundles/enterprise-legacy-learner-home/types/programCommon';
import { getProductId } from 'bundles/enterprise-legacy-learner-home/utils/ProductUtils';
import filterExistsOrDefault from 'bundles/enterprise-legacy-learner-home/utils/filterExistsOrDefault';
import mapExists from 'bundles/enterprise-legacy-learner-home/utils/mapExists';
import { getIdFromProductId } from 'bundles/enterprise-legacy-learner-home/utils/programCommonUtils';
import SiteMiscExperiments from 'bundles/epic/clients/siteMisc';
import { SKILL_LEVEL_SEGMENTS, getLevelName } from 'bundles/learner-skills/utils';
import { getRecommendationsPageChecklist } from 'bundles/levelsets/levelsets-v2/constants';
import { getSupportedSkills as getMasteryLevelEligibleSkills } from 'bundles/levelsets/levelsets-v2/utils/supportedSkills';
import { useNaptime } from 'bundles/naptimejs';
import ProgramBrowsingExperiencesV1 from 'bundles/naptimejs/resources/programBrowsingExperiences.v1';
import type ThirdPartyOrganizationsV1 from 'bundles/naptimejs/resources/thirdPartyOrganizations.v1';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import ObjectivesBreadcrumb from 'bundles/program-skills-objectives/components/ObjectivesBreadcrumb/ObjectivesBreadcrumb';
import LevelSetBanner from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/LevelSetBanner';
import LevelSetsResultModal from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/LevelSetsResultModal/LevelSetsResultModal';
import { MasteryLevelBanner } from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/MasteryLevelBanner';
import MobileDesktopProductFilters from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/MobileDesktopProductFilters';
import RelatedSkillsBanner from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/RelatedSkillsBanner';
import type {
  CollectionResult,
  PropsFromFullSkillCollectionGraphql,
  PropsFromPreviewSkillCollectionGraphql,
} from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/SkillCollectionDataProvider';
import {
  withFullSkillCollectionBySkill,
  withSkillCollectionPreviewBySkill,
} from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/SkillCollectionDataProvider';
import filterProducts from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/filterProducts';
import type {
  CourseElement,
  FilterDropdownOptionsMap,
  ProductItem,
  S12nElement,
} from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/generateFilterDropdownOptions';
import { generateFilterDropdownOptions } from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/generateFilterDropdownOptions';
import useProductFilterState from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/useProductFilterState';
import type { PropsFromGetSkillsQuery } from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/withSkillDetails';
import withSkillDetails from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/withSkillDetails';
import type { PropsFromSkillScoreGraphql } from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/withSkillScore';
import withSkillScore from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/withSkillScore';
import type { PropsFromLevelSetAttemptGraphql } from 'bundles/program-skills-objectives/components/withLevelSetAttempts';
import withLevelSetsAttempts from 'bundles/program-skills-objectives/components/withLevelSetAttempts';
import { castToProduct, redirectTo } from 'bundles/program-skills-objectives/utils';
import ProgressTracker from 'bundles/skills-common/private/progress-tracker/ProgressTracker';
import { ProgressTrackerMyScorePopoverWithScore } from 'bundles/skills-common/private/progress-tracker/ProgressTrackerMyScorePopover';
import { FeedbackButton, usePreviousFeedback } from 'bundles/skillset-learner-page';
import Skeleton from 'bundles/unified-home-common/components/shared/Skeleton';

import _t from 'i18n!nls/program-skills-objectives';

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps<ReactRouterLink>>(
  // TODO(ppaskaris): Ask how to type this.
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  Button
);

export type PropsFromCaller = {
  userId: number;
  programId: string;
  skillId: string;
  targetScore?: number;
  onProductCardClick?: (product: Product, productType: ProductType, collectionId?: string | null) => void;
  defaultPageSize?: number; // This prop is used to set states for test purposes
  defaultCurrentPage?: number; // This prop is used to set states for test purposes
  skillSetSlug?: string;
  programSlug: string;
  thirdPartyOrganization: ThirdPartyOrganizationsV1;
  programName: string;
  skillSetName?: string;
  isAuthenticatedUser?: boolean;
};

type PropsFromRetracked = {
  _eventData: TrackingContextData;
};

type PropsFromRouter = {
  replace: (path: string) => void;
  location: Location;
};

type PropsFromCollectionWithProps = {
  /**
   * The full collection if loaded, otherwise the preview collection if loaded, otherwise undefined
   */

  collection: CollectionResult | null | undefined;
};

export type Props = PropsFromCaller &
  PropsFromFullSkillCollectionGraphql &
  PropsFromPreviewSkillCollectionGraphql &
  PropsFromLevelSetAttemptGraphql &
  PropsFromSkillScoreGraphql &
  PropsFromRetracked &
  PropsFromGetSkillsQuery & { feedbackContext: string } & PropsFromRouter &
  PropsFromCollectionWithProps;

type PlaceholderProps = {
  skillSetSlug?: string;
  programSlug: string;
  thirdPartyOrganization: ThirdPartyOrganizationsV1;
  programName: string;
  showMasteryLevel?: boolean;
};

const useStyles = () => {
  return {
    headerContainer: css`
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      margin-bottom: calc(var(--cds-spacing-100) * -1);
      ${breakpoints.down('sm')} {
        flex-direction: column;
      }
    `,
    titleDescriptionContainer: css`
      display: flex;
      flex-direction: column;
      flex: 1;
      max-width: 600px;
      padding-right: var(--cds-spacing-200);
      margin-bottom: var(--cds-spacing-100);
    `,
    progressContainer: css`
      flex: 1 1 320px;
      min-width: 220px;
      max-width: 500px;
      display: flex;
      flex-direction: column;
      margin-bottom: var(--cds-spacing-100);

      .skills-collection-list-skill-score-help {
        align-self: flex-end;
        margin-bottom: var(--cds-spacing-100);
      }
      ${breakpoints.down('sm')} {
        max-width: 100%;
        flex: 1;
      }
    `,
    description: css`
      margin-bottom: var(--cds-spacing-400);
    `,
    levelSetBannerPlaceholder: css`
      height: 50px;
      width: 400px;
      float: right;
      ${breakpoints.down('sm')} {
        width: 100%;
        float: none;
      }
    `,
    skillScoreHelpContainer: css`
      margin: var(--cds-spacing-200) 0;
    `,
    filterPlaceholderPreviewCollection: css`
      margin-bottom: var(--cds-spacing-300);
      height: 150px;
      ${breakpoints.down('sm')} {
        height: 200px;
      }
      ${breakpoints.down('xs')} {
        width: 150px;
        height: 50px;
      }
    `,
    filterPlaceholderFullCollection: css`
      height: 116px;
      ${breakpoints.down('sm')} {
        height: 200px;
      }
      ${breakpoints.down('xs')} {
        width: 150px;
        height: 50px;
      }
    `,
    divider: css`
      margin: var(--cds-spacing-300) 0 var(--cds-spacing-600) 0;
    `,
    title: css`
      margin-bottom: var(--cds-spacing-200);
    `,
    titleFullWidth: css`
      width: 100%;
      margin-bottom: var(--cds-spacing-100);
    `,
    backNav: css`
      margin: var(--cds-spacing-300) 0 var(--cds-spacing-100) 0;
    `,
    backButton: css`
      margin-left: calc(var(--cds-spacing-100) * -1);
    `,
    titlePlaceholder: css`
      margin-bottom: var(--cds-spacing-200);
    `,
    descriptionPlaceholder: css`
      height: 80px;
    `,
    levelTag: css`
      margin-left: var(--cds-spacing-150);
    `,
    recommendationsTitlePlaceholder: css`
      height: 50px;
      margin-bottom: var(--cds-spacing-200);
    `,
    recommendationsChecklist: (hasDescription: boolean) => css`
      margin-top: ${hasDescription ? 'var(--cds-spacing-200)' : '0'};
      padding: var(--cds-spacing-200) var(--cds-spacing-300) var(--cds-spacing-200) 0;
    `,
    recommendationsChecklistItem: css`
      display: flex;
      align-items: center;
      padding-bottom: var(--cds-spacing-100);
    `,
  };
};

export const SkillPageContentPlaceholder = ({
  skillSetSlug,
  programSlug,
  thirdPartyOrganization,
  programName,
  showMasteryLevel = false,
}: PlaceholderProps) => {
  const styles = useStyles();
  const isMobile = useMediaQuery(breakpoints.down('sm'));

  return (
    <div data-testid="skill-page-content-placeholder">
      <ObjectivesBreadcrumb.Placeholder
        objectiveSlug={skillSetSlug}
        programSlug={programSlug}
        thirdPartyOrgSlug={thirdPartyOrganization.slug}
        thirdPartyOrganizationId={thirdPartyOrganization.id}
        programName={programName}
      />
      {!!skillSetSlug && (
        <div css={styles.backNav}>
          <TrackedButton
            {...Button.defaultProps}
            variant="ghost"
            size="small"
            icon={<ChevronPreviousIcon />}
            iconPosition="before"
            component={ReactRouterLink}
            trackingName="skillset_skillsets_link"
            to={`/programs/${programSlug}/skillsets/${skillSetSlug}`}
            css={styles.backButton}
          >
            {_t('Back to SkillSet')}
          </TrackedButton>
        </div>
      )}
      <div css={styles.headerContainer}>
        <Typography variant="d2" component="div" css={styles.titleDescriptionContainer}>
          <Skeleton width="400px" bonusCSS={styles.titlePlaceholder} />
          <Skeleton width="600px" bonusCSS={styles.descriptionPlaceholder} />
        </Typography>
        <div
          data-testid={`levelset-placeholder-${showMasteryLevel ? 'mastery' : 'progress-tracker'}`}
          css={styles.progressContainer}
        >
          {!showMasteryLevel && <ProgressTracker.Placeholder />}
          <Skeleton bonusCSS={styles.levelSetBannerPlaceholder} />
        </div>
      </div>
      <Divider css={styles.divider} />
      <Skeleton width="600px" bonusCSS={styles.recommendationsTitlePlaceholder} />
      <Skeleton width="100%" bonusCSS={styles.filterPlaceholderPreviewCollection} />
      <Col className="rc-CollectionItem">
        <Row rootClassName="CollectionItemPlaceholder p-b-1">
          {[0, 1, 2, 3].map(() => (
            <Col
              rootClassName="p-b-1"
              xs={12}
              sm={6}
              md={6}
              lg={4}
              xl={3}
              data-testid="skill-page-content-product-placeholder"
            >
              <CdsProductCard
                variant={isMobile ? 'list' : 'grid'}
                productType="course"
                loading={true}
                experimentVariant={SiteMiscExperiments.preview('productCardTest1')}
              />
            </Col>
          ))}
        </Row>
      </Col>
    </div>
  );
};

const masteryLevelEligibleSkillIds = new Set(getMasteryLevelEligibleSkills().map((skill) => skill.skillId));

export const SkillPageContentFC = ({
  fullCollection,
  isPreviewCollectionLoading,
  isFullCollectionLoading,
  onProductCardClick,
  levelSetAttempts,
  levelSetAttemptLoading,
  skillScore,
  defaultPageSize = 24,
  defaultCurrentPage = 1,
  skillId,
  skill,
  targetScore,
  skillSetSlug,
  programSlug,
  programId,
  thirdPartyOrganization,
  programName,
  skillSetName,
  isSkillLoading,
  isAuthenticatedUser,
  _eventData = {},
  replace,
  location,
  userId,
  feedbackContext,
  collection,
  recsEventingData,
}: Props) => {
  const isAssessmentComplete = location.query.assessmentCompleted === 'true';
  const showMasteryLevel = location.query.mastery === 'true' && masteryLevelEligibleSkillIds.has(skillId);
  const styles = useStyles();
  const [isSkillScoreHelpExpanded, setIsSkillScoreHelpExpanded] = React.useState(false);
  const [pageSize, setPageSize] = React.useState(defaultPageSize);
  const [currentPage, setCurrentPage] = React.useState(defaultCurrentPage);
  const windowPositionRef = React.useRef<number | null>(null);
  const [isMobileFiltersModalOpen, setIsMobileFiltersModalOpen] = React.useState(false);
  const [isLevelSetAssessmentModalOpen, setIsLevelSetAssessmentModalOpen] = React.useState(isAssessmentComplete);
  const productFilterState = useProductFilterState();
  const isMobile = useMediaQuery(breakpoints.down('xs'));
  const browseExperienceRequest = useNaptime<{ browseExperience: ProgramBrowsingExperiencesV1 }>(() => {
    return {
      browseExperience: ProgramBrowsingExperiencesV1.get(programId),
    };
  }, []);
  const { giveFeedback, recommendationFeedback } = usePreviousFeedback({ userId, collection, feedbackContext });

  const shouldShowSkillBasedClipsCollection =
    !!browseExperienceRequest?.data?.browseExperience?.isInDeepBrowseMode &&
    isAuthenticatedUser &&
    SKILLS_IDS_FOR_CLIPS_ALLOW_LIST.includes(skill?.skillId ?? '');

  const toggleSkillScoreHelp = () => {
    setIsSkillScoreHelpExpanded(!isSkillScoreHelpExpanded);
  };

  // This is a hack to get the window to stay where it is when the page size changes
  React.useEffect(() => {
    if (windowPositionRef.current !== null) {
      window.scrollTo(0, windowPositionRef.current);
      windowPositionRef.current = null;
    }
  }, [pageSize]);

  React.useEffect(() => {
    // Redirect to search if there are less than 4 recs
    if (!isFullCollectionLoading && fullCollection && !isSkillLoading && fullCollection.items.length < 4) {
      Retracked.trackComponent(
        _eventData,
        {
          skillId,
          collectionLength: fullCollection.items.length,
        },
        'empty_skill_page',
        'load'
      );
      // For invalid skills we won't have a skill name so we put skill ID as a back up
      const searchUrl = `/programs/${programSlug}/browse?query=${skill?.skillName ?? skillId}`;
      redirectTo(searchUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullCollectionLoading, fullCollection, skill?.skillName, isSkillLoading]);

  // The index for the first item in the page
  const PAGE_OFFSET = (currentPage - 1) * pageSize;

  if (isPreviewCollectionLoading || !collection || isSkillLoading || !skill) {
    return (
      <SkillPageContentPlaceholder
        skillSetSlug={skillSetSlug}
        programSlug={programSlug}
        thirdPartyOrganization={thirdPartyOrganization}
        programName={programName}
        showMasteryLevel={showMasteryLevel}
      />
    );
  }

  const { trackId: collectionId, courses, s12ns, items } = collection;

  // Only show level set banners if there are level set attempts remaining
  const shouldShowLevelSetBanners = !levelSetAttemptLoading && !!levelSetAttempts?.[0];
  const shouldShowLargeLevelSetBanner =
    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    shouldShowLevelSetBanners && currentPage === 1 && levelSetAttempts[0].attemptsLeft !== 0;

  const courseProducts: ProductItem[] = filterExistsOrDefault(courses?.elements).map((product) => ({
    isCourse: true,
    // There is a type difference between preview and full collections
    product: product as CourseElement,
    id: product.id,
  }));
  const s12nProducts: ProductItem[] = filterExistsOrDefault(s12ns?.elements).map((product) => {
    return {
      isCourse: false,
      // There is a type difference between preview and full collections
      product: product as S12nElement,
      id: product.id,
    };
  });

  let products: ProductItem[] = [...courseProducts, ...s12nProducts];

  if (items) {
    const productsById = keyBy(products, (product) => product.id);
    // @ts-expect-error TS2538 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    products = mapExists(items, (item) => productsById[getIdFromProductId(item?.productId ?? '')]);
  }

  const onProductCardClick0 = (product: Product, productType: ProductType) => {
    Retracked.trackComponent(
      _eventData,
      {
        productType,
        productId: product.id,
      },
      'skills_page_product_card',
      'click'
    );
    onProductCardClick?.(product, productType, collection.trackId);
  };

  /**
   * Filtering and generating the filter options is a little complex. It is particularly fragile to unexpected results from the api.
   * Therefore we make sure to handle any errors so we only disable filtering instead of crashing the whole page.
   */
  let filteredProducts;
  let filterDropdownOptions;
  let filterDropdownError;
  try {
    filteredProducts = filterProducts(products, productFilterState.filterValues);
    filterDropdownOptions = generateFilterDropdownOptions(filteredProducts, productFilterState.filterValues);
  } catch (filterError) {
    loggerSingleton.error('Could not setup filters in SkillPageContent');
    Sentry.captureException(filterError);
    filterDropdownOptions = {} as FilterDropdownOptionsMap;
    filteredProducts = products;
    filterDropdownError = filterError;
  }

  // We split the array below into two arrays to insert the banner at the correct index
  // We reuse this function to map over the resulting two arrays
  const renderProductCard = (
    { product, isCourse, id }: ProductItem,
    index: number,
    options: { aboveFoldCount?: number }
  ) => {
    const productId = getProductId(id, isCourse);

    // The number of items to not render lazily
    const aboveFoldCount = options?.aboveFoldCount ?? 0;
    const productType = isCourse ? PRODUCT_TYPE.COURSE : PRODUCT_TYPE.S12N;
    const shouldShowListView = isMobile;
    const eventingV3ProductData = {
      id,
      name: product.name,
      type: productType,
      slug: skillSetSlug || '',
    };

    const productCard = (
      <ProductCardCds
        htmlAttributes={{
          'data-e2e': isCourse ? 'CourseProductCard' : 'S12nProductCard',
          'data-testid': 'skill-page-content-product-card',
        }}
        // Courses and S12ns dont have the same shape and require a casting
        product={castToProduct(product)}
        // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        productType={productType}
        variant={shouldShowListView ? 'list' : 'grid'}
        onClick={onProductCardClick0}
        label={getProductCardDisplayProps(castToProduct(product), !isCourse).label}
        eventingV3Data={{
          productCard: {
            index: index ?? 0,
            recommender: recsEventingData,
          },
          // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
          product: eventingV3ProductData,
        }}
        customLinkProps={{
          trackingName: 'collection_product_card',
          withVisibilityTracking: true,
          requireFullyVisible: true,
          data: { productCardOrder: index + 1, collectionId, productId },
        }}
        feedbackButton={
          <FeedbackButton
            feedbackContext={feedbackContext}
            isDarkCard={false}
            onFeedbackSent={giveFeedback}
            product={castToProduct(product)}
            skillName={skill.skillName}
            contentType={getProductCardDisplayProps(castToProduct(product), !isCourse).labelAsText}
            userId={userId}
            trackingData={{
              skillId,
              filters: productFilterState.filterValues,
              programSlug,
              skillSetSlug,
              url: location.pathname,
              contentId: id,
            }}
            previousFeedback={recommendationFeedback[product.id] ?? ''}
          />
        }
      />
    );
    /**
     * This data is fetched in SSR and to optimise for LCP we want to lazy fetch everything below the fold
     * but make sure the content above the fold is not slowed down by a lazy render
     */
    return (
      <Col rootClassName="p-b-1" key={id} xs={12} sm={6} md={6} lg={4} xl={3}>
        {index < aboveFoldCount ? (
          productCard
        ) : (
          <LazyRender
            offset={300}
            placeholderComponent={
              <CdsProductCard
                variant="grid"
                productType="course"
                loading={true}
                experimentVariant={SiteMiscExperiments.preview('productCardTest1')}
              />
            }
            debounce={50}
            throttle={undefined}
          >
            {productCard}
          </LazyRender>
        )}
      </Col>
    );
  };

  const filteredProductsWithoutDownvoted = filteredProducts.filter(
    (product) => recommendationFeedback[product.id] !== 'Hidden'
  );
  const filteredProductsDownvoted = filteredProducts.filter(
    (product) => recommendationFeedback[product.id] === 'Hidden'
  );
  const filteredProductsSortedByDownvotes = [...filteredProductsWithoutDownvoted, ...filteredProductsDownvoted];
  const productsForCurrentPage = filteredProductsSortedByDownvotes.slice(PAGE_OFFSET, PAGE_OFFSET + pageSize);
  return (
    <div data-e2e="skill-page-content">
      {isLevelSetAssessmentModalOpen && (
        <LevelSetsResultModal
          skillId={skillId}
          skillName={skill?.skillName}
          onCloseUpdatedModal={() => {
            setIsLevelSetAssessmentModalOpen(false);
            // Replace the pathname to remove the `assessmentCompleted=true` from the query
            replace(location.pathname);
          }}
        />
      )}
      <ObjectivesBreadcrumb
        objectiveSlug={skillSetSlug}
        objectiveName={skillSetName}
        programSlug={programSlug}
        thirdPartyOrgSlug={thirdPartyOrganization.slug}
        thirdPartyOrganizationId={thirdPartyOrganization.id}
        programName={programName}
        skillName={skill?.skillName}
        showSkillDashboard={!skillSetSlug && !showMasteryLevel}
      />
      {!!skillSetSlug && (
        <div css={styles.backNav}>
          <TrackedButton
            {...Button.defaultProps}
            variant="ghost"
            size="small"
            icon={<ChevronPreviousIcon />}
            iconPosition="before"
            component={ReactRouterLink}
            trackingName="skillset_skillsets_link"
            to={`/programs/${programSlug}/skillsets/${skillSetSlug}`}
            css={styles.backButton}
          >
            {_t('Back to SkillSet')}
          </TrackedButton>
        </div>
      )}
      <div css={styles.headerContainer}>
        {showMasteryLevel && (
          <Heading defaultLevel={1} css={styles.titleFullWidth}>
            <FormattedMessage message="{skillName} recommendations for you" skillName={skill?.skillName} />
          </Heading>
        )}
        <div css={styles.titleDescriptionContainer}>
          {!showMasteryLevel && (
            <Heading defaultLevel={1} css={styles.title}>
              {skill?.skillName}
            </Heading>
          )}
          {!!skill?.skillDescription && (
            <Typography2 component="p" color="supportText">
              {skill?.skillDescription}
            </Typography2>
          )}
          {showMasteryLevel && (
            <ul css={styles.recommendationsChecklist(!!skill?.skillDescription)}>
              {getRecommendationsPageChecklist().map((item) => (
                <li key={item.textContent} css={styles.recommendationsChecklistItem}>
                  <CheckIcon
                    role="presentation"
                    size="small"
                    css={css`
                      margin-right: var(--cds-spacing-200);
                    `}
                  />
                  {item.linkText ? (
                    <Typography2 component="p" variant="bodyPrimary" color="body">
                      <FormattedMessage
                        message={_t('{textContent} {linkContent}')}
                        textContent={item.textContent}
                        linkContent={
                          <a
                            href="https://docs.google.com/document/d/1feHy5_fS4fHAcD6VGi2mVfEAa5ExgS05dhDqe09Pb80"
                            target="_blank"
                            rel="noopener noreferrer"
                            css={css`
                              color: var(--cds-color-neutral-primary);
                            `}
                          >
                            {item.linkText}
                          </a>
                        }
                      />
                    </Typography2>
                  ) : (
                    <Typography2 component="p" variant="bodyPrimary" color="body">
                      {item.textContent}
                    </Typography2>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
        {!showMasteryLevel && (
          <div css={styles.progressContainer}>
            <ProgressTracker
              hideTarget={!targetScore}
              target={targetScore}
              score={skillScore}
              segments={SKILL_LEVEL_SEGMENTS}
              afterPersonalScore={skillScore ? <Tag css={styles.levelTag}>{getLevelName(skillScore)}</Tag> : undefined}
              endOfHeader={
                <div
                  css={css`
                    margin-left: auto;
                  `}
                >
                  <InformationCardToggle
                    isOpen={isSkillScoreHelpExpanded}
                    onClick={toggleSkillScoreHelp}
                    isCdsEnabled
                    label={_t('How does scoring work?')}
                    trackingName="skill_targets_information_link"
                    rootClassName="skills-collection-list-skill-score-help"
                  />
                </div>
              }
              scoreTooltip={
                <ProgressTrackerMyScorePopoverWithScore
                  score={skillScore ?? 0}
                  skillId={skill.skillId}
                  skillName={skill?.skillName ?? ''}
                />
              }
            />
            {shouldShowLevelSetBanners && (
              <TrackedDiv
                withVisibilityTracking={true}
                trackingName="small_level_set_banner"
                data={{ skillId: skill.skillId, showMasteryLevel }}
              >
                {/*
                 // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess */}
                <LevelSetBanner skillId={skillId} variant="small" levelSetAttempt={levelSetAttempts?.[0]} />
              </TrackedDiv>
            )}
          </div>
        )}
        {showMasteryLevel && (
          <ErrorBoundaryWithTagging componentName="MasteryLevelBanner">
            <div css={styles.progressContainer}>
              <MasteryLevelBanner skillId={skill.skillId} skillName={skill.skillName} />
            </div>
          </ErrorBoundaryWithTagging>
        )}
      </div>
      {!showMasteryLevel && (
        <React.Fragment>
          <div css={styles.skillScoreHelpContainer}>
            <SkillTargetsInformationCard
              hideTarget={!targetScore}
              isOpen={isSkillScoreHelpExpanded}
              onRequestClose={toggleSkillScoreHelp}
              hasProficiencyTargetsTab={false}
            />
          </div>
          <Divider css={styles.divider} />
        </React.Fragment>
      )}
      {shouldShowSkillBasedClipsCollection && (
        <SkillsClipsCollection
          programId={programId}
          onProductCardClick={onProductCardClick0}
          isAuthenticatedUser={isAuthenticatedUser ?? false}
          skillNames={[skill.skillName]}
          translatedTitleText={_t('Videos about #{skillName} recommended for you', { skillName: skill.skillName })}
          collectionId="clips-collection-skills-page"
        />
      )}
      {shouldShowSkillBasedClipsCollection && <Divider css={styles.divider} />}
      {!showMasteryLevel && (
        <div>
          <Typography variant="h2semibold">
            {skill.isEligibleForScoring
              ? _t('Recommendations based on your current skill level')
              : _t('Recommendations for this skill')}
          </Typography>
          <Typography2 component="p" color="supportText" css={styles.description}>
            {_t('Improve your skills with any one of these recommendations.')}
          </Typography2>
        </div>
      )}
      <Section initialLevel={2}>
        <Col className="rc-CollectionItem">
          <Row rootClassName="p-b-2">
            {!filterDropdownError && (
              <ErrorBoundaryWithTagging componentName="SkillsCollectionList.filterRow">
                <Col xs={12}>
                  {isFullCollectionLoading ? (
                    <Skeleton
                      width="100%"
                      bonusCSS={styles.filterPlaceholderFullCollection}
                      htmlAttributes={{
                        'data-testid': 'filter-full-collection-placeholder',
                      }}
                    />
                  ) : (
                    <MobileDesktopProductFilters
                      filterDropdownOptions={filterDropdownOptions}
                      filterValues={productFilterState.filterValues}
                      setFilterValue={(...args) => {
                        productFilterState.setFilterValue(...args);
                        setCurrentPage(1);
                      }}
                      resetFilterValues={productFilterState.resetFilterValues}
                      setIsMobileFiltersModalOpen={setIsMobileFiltersModalOpen}
                      isMobileFiltersModalOpen={isMobileFiltersModalOpen}
                    />
                  )}
                </Col>
              </ErrorBoundaryWithTagging>
            )}
          </Row>
          <Row rootClassName="p-b-1">
            {productsForCurrentPage
              .slice(0, 8)
              .map((item, index) => renderProductCard(item, index, { aboveFoldCount: 4 }))}
          </Row>
          <Row rootClassName="p-b-1">
            <Col xs={12} rootClassName="p-b-1">
              <LazyRender
                offset={300}
                placeholderComponent={<Skeleton width="100%" bonusCSS={css({ height: 180 })} />}
                debounce={50}
                throttle={undefined}
              >
                <TrackedDiv withVisibilityTracking={true} trackingName="related_skills_banner" data={{ skillId }}>
                  <RelatedSkillsBanner
                    programSlug={programSlug}
                    skillSetSlug={skillSetSlug}
                    currentSkillId={skillId}
                    skillName={skill.skillName}
                  />
                </TrackedDiv>
              </LazyRender>
            </Col>
          </Row>
          <Row rootClassName="p-b-1">
            {productsForCurrentPage
              .slice(8)
              .map((item, index) => renderProductCard(item, index, { aboveFoldCount: 0 }))}
          </Row>
          <Row css={css({ marginBottom: 64, justifyContent: 'center', display: 'flex' })}>
            <Pagination
              total={filteredProductsSortedByDownvotes.length}
              pageSize={pageSize}
              page={currentPage}
              onPageSizeChange={(e, size) => {
                if (size > pageSize) {
                  // We only want to maintain scroll position if we are increasing the page size
                  windowPositionRef.current = window.scrollY;
                }
                setPageSize(size);
              }}
              onPageChange={(e, page) => {
                setCurrentPage(page);
                window.scrollTo(0, 0);
              }}
              // Multiples of 4 as the rows are 4 products wide
              pageSizeInputOptions={[
                { label: '24', value: 24 },
                { label: '48', value: 48 },
              ]}
            />
          </Row>
          {levelSetAttempts?.[0] && shouldShowLargeLevelSetBanner && (
            <Row rootClassName="p-b-1">
              <Col xs={12} rootClassName="p-b-1">
                <LazyRender
                  offset={300}
                  placeholderComponent={<Skeleton width="100%" bonusCSS={css({ height: 180 })} />}
                  debounce={50}
                  throttle={undefined}
                >
                  <TrackedDiv withVisibilityTracking={true} trackingName="large_level_set_banner" data={{ skillId }}>
                    <LevelSetBanner skillId={skillId} variant="large" levelSetAttempt={levelSetAttempts?.[0]} />
                  </TrackedDiv>
                </LazyRender>
              </Col>
            </Row>
          )}
        </Col>
      </Section>
    </div>
  );
};

export const SkillPageContent = compose<Props, PropsFromCaller>(
  withSkillCollectionPreviewBySkill,
  withProps(({ skillId }: PropsFromCaller) => ({ feedbackContext: `skill~${skillId}` })),
  withSkillScore,
  withSkillDetails,
  // withLevelSetAttempts takes an array of skill ids so just map our single skill id to an array
  withProps(({ skillId }: PropsFromCaller & PropsFromPreviewSkillCollectionGraphql) => ({
    skillIds: [skillId],
  })),
  withLevelSetsAttempts(),
  withFullSkillCollectionBySkill,
  withProps(
    ({
      previewCollection,
      fullCollection,
    }: PropsFromCaller &
      PropsFromPreviewSkillCollectionGraphql &
      PropsFromFullSkillCollectionGraphql): PropsFromCollectionWithProps => ({
      // Prioritise fullCollection once loaded
      collection: fullCollection || previewCollection,
    })
  ),
  withRetrackedContextAsProps(),
  connectToRouter((router) => ({
    replace: router.replace,
    location: router.location,
  }))
)(SkillPageContentFC);

// It's important this is the outer most component so it catches errors in our HOCS
const SkillPageContentWithErrorBoundary = (props: PropsFromCaller) => (
  <ErrorBoundaryWithTagging
    componentName="SkillPageContent"
    errorComponent={
      <InlineNotification severity="error" role="alert">
        {_t('Sorry! Something went wrong. Please refresh the page.')}
      </InlineNotification>
    }
  >
    <SkillPageContent {...props} />
  </ErrorBoundaryWithTagging>
);

export default SkillPageContentWithErrorBoundary;
